"use client";

import type { Voice as PayloadVoice } from "@mw/shared/payload-types";
import type { Voice } from "@/app/_data/voices";
import type { FC } from "react";

import { useState, useMemo, useEffect } from "react";

import { transformPayloadVoice } from "@/app/_utilities/payload";
import { TranscriptionContent } from "@ui/components/demos/AudioBook/TranscriptionContent";

export interface AudioBookDemoProps {
  /** The text content to be converted to speech */
  text: string;
  /** Chapter title displayed above the text */
  chapterTitle?: string | null;
  /** Available voices for the voice selector (Payload Voice relationships) */
  voices: (string | PayloadVoice)[] | null;
}

const AudioBookDemo: FC<AudioBookDemoProps> = ({ text, chapterTitle, voices: payloadVoices }) => {
  // Transform payload voices to the Voice type used by TranscriptionContent
  const voices = useMemo(() => {
    if (!payloadVoices) return [];
    return payloadVoices.filter((v): v is PayloadVoice => typeof v !== "string").map(transformPayloadVoice);
  }, [payloadVoices]);

  const [selectedVoice, setSelectedVoice] = useState<Voice | undefined>(() => voices[0]);

  // Update selected voice when voices array changes (e.g., on initial load or hydration)
  useEffect(() => {
    if (voices.length > 0 && !selectedVoice) {
      setSelectedVoice(voices[0]);
    }
  }, [voices, selectedVoice]);

  if (!text) {
    return null;
  }

  return (
    <div className='tw-h-full tw-w-full tw-flex tw-items-center tw-justify-center'>
      <div className='tw-h-[90%] tw-px-8 sm:tw-px-16 tw-pt-6 tw-flex tw-flex-col'>
        <TranscriptionContent
          text={text}
          chapterTitle={chapterTitle ?? undefined}
          voices={voices}
          selectedVoice={selectedVoice}
          onVoiceChange={setSelectedVoice}
          voiceSelectorWidthClassName='tw-w-40'
        />
      </div>
    </div>
  );
};

export default AudioBookDemo;
